// Global audio manager with crossfade and Now Playing state.
// Every component that plays audio registers through this manager.

const FADE_MS = 400;
const FADE_STEPS = 20;

let _currentTrack = null;   // { title, subtitle, audio, stopFn }
let _subscribers = new Set();
const _stopCallbacks = new Set();

// --- Crossfade helper ---
function fadeOut(audio) {
  return new Promise((resolve) => {
    if (!audio || audio.paused) { resolve(); return; }
    const startVol = audio.volume;
    const step = startVol / FADE_STEPS;
    const interval = FADE_MS / FADE_STEPS;
    let current = startVol;
    const id = setInterval(() => {
      current -= step;
      if (current <= 0.01) {
        clearInterval(id);
        audio.volume = 0;
        audio.pause();
        audio.volume = startVol; // restore for next play
        resolve();
      } else {
        audio.volume = current;
      }
    }, interval);
  });
}

// --- Notify subscribers of state change ---
function _notify() {
  const state = getNowPlaying();
  _subscribers.forEach((fn) => fn(state));
}

// --- Public API ---

/** Subscribe to now-playing changes. Returns unsubscribe function. */
function subscribe(fn) {
  _subscribers.add(fn);
  return () => _subscribers.delete(fn);
}

/** Get current now-playing state */
function getNowPlaying() {
  if (!_currentTrack) return null;
  return {
    title: _currentTrack.title,
    subtitle: _currentTrack.subtitle,
    audio: _currentTrack.audio,           // can be null for Spotify embeds
    isPlaying: _currentTrack.audio ? !_currentTrack.audio.paused : true,
  };
}

/** Register a stop callback (for components that play audio). */
function register(stopFn) {
  _stopCallbacks.add(stopFn);
  return () => _stopCallbacks.delete(stopFn);
}

/** Unregister a stop callback. */
function unregister(stopFn) {
  _stopCallbacks.delete(stopFn);
}

/** Stop everything (no crossfade). */
function stopAll(except) {
  _stopCallbacks.forEach((stop) => {
    if (stop !== except) stop();
  });
  if (!except) { _currentTrack = null; _notify(); }
}

/**
 * Request playback — crossfades out the old source, registers the new one.
 * @param {Function} stopFn - callback to stop this source
 * @param {{ title: string, subtitle?: string, audio?: HTMLAudioElement }} meta
 */
async function requestPlayback(stopFn, meta = {}) {
  // Fade out the previous audio if it exists
  if (_currentTrack && _currentTrack.audio && _currentTrack.stopFn !== stopFn) {
    const oldAudio = _currentTrack.audio;
    const oldStop = _currentTrack.stopFn;
    // Start fading out
    fadeOut(oldAudio).then(() => {
      // After fade, invoke the old stopFn (which resets state in the component)
      _stopCallbacks.forEach((stop) => {
        if (stop !== stopFn && stop === oldStop) stop();
      });
    });
  } else {
    // No audio to fade — just hard-stop other sources
    _stopCallbacks.forEach((stop) => {
      if (stop !== stopFn) stop();
    });
  }

  _stopCallbacks.add(stopFn);
  _currentTrack = {
    title: meta.title || 'Now Playing',
    subtitle: meta.subtitle || '',
    audio: meta.audio || null,
    stopFn,
  };
  _notify();
}

/** Called when a component stops itself (e.g., track ended, user clicked pause). */
function clearNowPlaying(stopFn) {
  if (_currentTrack && _currentTrack.stopFn === stopFn) {
    _currentTrack = null;
    _notify();
  }
}

/** Toggle play/pause on the current track from the Now Playing bar. */
function togglePlayPause() {
  if (!_currentTrack || !_currentTrack.audio) return;
  const audio = _currentTrack.audio;
  if (audio.paused) {
    audio.play().catch(() => {});
  } else {
    audio.pause();
  }
  _notify();
}

/** Stop everything from the Now Playing bar. */
function stopFromBar() {
  if (_currentTrack) {
    const audio = _currentTrack.audio;
    if (audio) {
      fadeOut(audio).then(() => {
        stopAll();
        _notify();
      });
    } else {
      stopAll();
      _notify();
    }
  }
}

export const audioManager = {
  requestPlayback,
  register,
  unregister,
  stopAll,
  subscribe,
  getNowPlaying,
  clearNowPlaying,
  togglePlayPause,
  stopFromBar,
};
